import { useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth";

const ADMIN_PATHS = ["/admin/account", "/admin/databases", "/admin/oauth", "/admin/context"];

export function Layout() {
  const { me, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const onAdminRoute = ADMIN_PATHS.some((p) => location.pathname.startsWith(p));
  const [adminOpen, setAdminOpen] = useState(onAdminRoute);

  const onLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="h-screen flex bg-slate-50 overflow-hidden">
      <aside className="w-52 shrink-0 bg-slate-900 text-white flex flex-col h-screen">
        <div className="h-14 flex items-center px-4 border-b border-slate-800">
          <span className="font-semibold text-sm tracking-tight">Kepler MCP</span>
        </div>

        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5 text-[13px]">
          <NavItem to="/">Dashboard</NavItem>
          <NavItem to="/me/grants">My Access</NavItem>
          <NavItem to="/me/files">My Files</NavItem>

          {me?.is_admin && (
            <div className="pt-2">
              <button
                type="button"
                onClick={() => setAdminOpen((v) => !v)}
                className="w-full flex items-center justify-between px-3 py-1.5 rounded text-slate-300 hover:text-white hover:bg-slate-800/60 uppercase tracking-wide text-[11px] font-semibold"
              >
                <span>Admin Tools</span>
                <span aria-hidden className={`transition-transform ${adminOpen ? "rotate-90" : ""}`}>›</span>
              </button>
              {adminOpen && (
                <div className="mt-0.5 space-y-0.5">
                  <NavItem to="/admin/account" sub>Account Management</NavItem>
                  <NavItem to="/admin/databases" sub>Database Management</NavItem>
                  <NavItem to="/admin/oauth" sub>OAuth Management</NavItem>
                  <NavItem to="/admin/context" sub>Context</NavItem>
                </div>
              )}
            </div>
          )}
        </nav>

        <div className="border-t border-slate-800 p-3 text-[12px]">
          {me && (
            <div className="mb-2 min-w-0">
              <div className="truncate text-slate-200" title={me.username}>{me.username}</div>
              {me.is_admin && (
                <div className="mt-1">
                  <span
                    className="inline-flex items-center gap-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-400/40 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                    title="Administrator"
                  >
                    <span aria-hidden>★</span>admin
                  </span>
                </div>
              )}
            </div>
          )}
          <button
            onClick={onLogout}
            type="button"
            className="w-full inline-flex items-center justify-center px-3 py-1.5 rounded text-[12px] font-medium border border-slate-700 bg-slate-800 text-white hover:bg-slate-700"
          >
            Sign Out
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col h-screen overflow-hidden">
        <main className="flex-1 w-full px-6 py-6 overflow-y-auto">
          <Outlet />
          <footer className="text-xs text-slate-500 text-center pt-6 pb-2">
            Kepler RDS MCP — admin portal. Surface for analysts is Claude (see runbook).
          </footer>
        </main>
      </div>
    </div>
  );
}

function NavItem({ to, children, sub = false }: { to: string; children: React.ReactNode; sub?: boolean }) {
  return (
    <NavLink
      to={to}
      end={to === "/"}
      className={({ isActive }) =>
        `block rounded px-3 py-1.5 ${sub ? "pl-6 text-[12px]" : ""} ${
          isActive
            ? "bg-slate-800 text-white border-l-2 border-indigo-400"
            : "text-slate-300 hover:text-white hover:bg-slate-800/60"
        }`
      }
    >
      {children}
    </NavLink>
  );
}
